import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ContentContainer from 'templates/content-container/contentContainer';
import ContentContainerToolbar from 'ui-components/molecules/content-container-toolbar/contentContainerToolbar';
import WeekNavigator from './components/weekNavigator';
import DispatchBoard from './components/dispatchBoard';
import SchedulerActionsMenu from './components/schedulerActionsMenu';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import UpdateWeek from './sidebars/updateWeek';
import { useWeeks } from './hooks/useWeek';
import { useLanesByDay } from './hooks/useLanes';
import { setSelectedScheduleType, setSelectedWeekStartDate, staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import './linehaulScheduler.scss';
import UpdateSidebar from './sidebars/updateSidebar';
import TargetWeek from './sidebars/targetWeek';
import { ReactComponent as PaperDocumentIcon } from "images/paper_document.svg";
import ActivityLogSidebar from './sidebars/activityLog';
import ReviewDispatchSidebar from './sidebars/reviewDispatch';
import WeekSummaryTiles from './components/weekSummaryTiles';
import { useSummaryTiles } from './hooks/useSummaryTiles';

// ── helpers ──────────────────────────────────────────────────────────────────

/** Return the Monday of the week containing `date` */
function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sun
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

/** Add `n` days to a date (returns a new Date) */
function addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

const LinehaulScheduler: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const staticLoadState = useSelector(staticLoadSelector);
    const dispatch = useDispatch();
    const { refetch: refetchWeeks } = useWeeks();

    const [isUpdateWeekOpen, setIsUpdateWeekOpen] = useState(false);
    const [isCreateLaneOpen, setIsCreateLaneOpen] = useState(false);
    const [isEditWeek, setIsEditWeek] = useState(false);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [showActivityLog, setShowActivityLog] = React.useState<boolean>(false);
    const [showReviewDispatch, setShowReviewDispatch] = React.useState<boolean>(false);

    const { data: weeks } = useWeeks();

    // 1. Derive navigable weeks from API
    const workingWeeks = React.useMemo(() => {
        if (!weeks) return [];
        return weeks
            // .filter((w) => w.scheduleType === 'WORKING')
            .map((w) => w.weekStartDate)
            .sort(); // 'YYYY-MM-DD' sorts chronologically
    }, [weeks]);

    // 2. Track which week is selected (by date string, not index — safer if list refetches)
    const [weekStartDate, setWeekStartDate] = useState<string>('');
    const [scheduleType, setScheduleType] = useState<string>('');

    const { refetch: refetchLanesByDay } = useLanesByDay(
        staticLoadState.selectedDivision,
        weekStartDate
    );

    const { data: summaryTiles, isLoading: isLoadingSummaryTiles, refetch: refetchSummaryTiles } = useSummaryTiles(
        staticLoadState.selectedDivision,
        weekStartDate,
        !!weekStartDate
    );

    // 3. Initialize once when weeks load
    useEffect(() => {
        if (workingWeeks.length > 0 && !weekStartDate) {
            const urlWeekStartDate = new URLSearchParams(location.search).get('weekStartDate');
            const initialWeek =
                urlWeekStartDate && workingWeeks.includes(urlWeekStartDate)
                    ? urlWeekStartDate
                    : workingWeeks[workingWeeks.length - 1];
            setWeekStartDate(initialWeek);
            setScheduleType(weeks?.find((w) => w.weekStartDate === initialWeek)?.scheduleType || '');
        }
    }, [workingWeeks, weekStartDate, location.search, weeks]);

    useEffect(() => {
        dispatch(setSelectedWeekStartDate(weekStartDate));
    }, [weekStartDate, dispatch]);

    useEffect(() => {
        dispatch(setSelectedScheduleType(scheduleType.toLowerCase().capitalize()));
    }, [scheduleType, dispatch]);

    const currentIndex = workingWeeks.indexOf(weekStartDate);
    const weekStart = weekStartDate ? new Date(weekStartDate + 'T00:00:00') : getMonday(new Date());
    const weekEnd = addDays(weekStart, 6);

    const handlePrev = () => {
        if (currentIndex > 0) {
            setWeekStartDate(workingWeeks[currentIndex - 1]);
            setScheduleType(weeks?.find((w) => w.weekStartDate === workingWeeks[currentIndex - 1])?.scheduleType || '');
        }
    };

    const handleNext = () => {
        if (currentIndex >= 0 && currentIndex < workingWeeks.length - 1) {
            setWeekStartDate(workingWeeks[currentIndex + 1]);
            setScheduleType(weeks?.find((w) => w.weekStartDate === workingWeeks[currentIndex + 1])?.scheduleType || '');
        }
    };

    const currentWeek = weeks?.find((w) => w.weekStartDate === weekStartDate);
    const editWeek = {
        weekStartDate: moment(weekStart).format('YYYY-MM-DD'),
        division: staticLoadState.selectedDivision,
        weekDescription: currentWeek?.description ?? '',
        status: currentWeek?.status ?? '',
        dispatches: currentWeek?.dispatches,
        createdAt: currentWeek?.createdAt,
        lastModifiedAt: currentWeek?.lastModifiedAt,
    };

    return <><ContentContainer mods={{ "full-width": true }}
        titleComponent={
            <div className='linehaul-scheduler-container-toolbar'>
                <ContentContainerToolbar
                    title="Linehaul Weekly Schedule">
                </ContentContainerToolbar>
            </div>
        }
    >
        <div className='linehaul-scheduler-container-toolbar-clone-existing-week'>
            <Button theme={ButtonThemes.grayInverted} size={ButtonSizes.auto} onClick={() => setModalOpen(true)}>Clone Existing Week</Button>
            <SchedulerActionsMenu
                onEditScheduleDetails={() => {
                    setIsEditWeek(true);
                    setIsUpdateWeekOpen(true);
                }}
                onCreateNewSchedule={() => {
                    setIsEditWeek(false);
                    setIsUpdateWeekOpen(true);
                }}
                showCreateLane={scheduleType === 'MASTER'}
                onCreateLane={() => setIsCreateLaneOpen(true)}
                onManageSchedules={() => history.push('/static-load/manage-schedules')}
            />
            <div onClick={() => setShowActivityLog(state => !state)} className='activity-log-icon'><PaperDocumentIcon />&nbsp; Activity Log</div>
        </div>
        <div className='linehaul-scheduler-container-week-navigator'>
            <div className='linehaul-scheduler-container-week-navigator-title'>
                <span>You are viewing the schedule for {moment(weekStart).format('MMMM D, YYYY')} - {moment(weekEnd).format('MMMM D, YYYY')}.
                    Changes apply only to this week.
                </span>
            </div>
            <div className='linehaul-scheduler-container-week-navigator-selector'>
                <div className='linehaul-scheduler-container-week-navigator-component'>
                    <WeekNavigator
                        startDate={weekStart}
                        endDate={weekEnd}
                        subtitle={`${staticLoadState.selectedScheduleType} week · Changes apply to this week only`}
                        onPrev={handlePrev}
                        onNext={handleNext}
                        prevDisabled={currentIndex <= 0}
                        nextDisabled={currentIndex < 0 || currentIndex >= workingWeeks.length - 1}
                    />
                </div>
                <div className='linehaul-scheduler-container-week-navigator-actions'>
                    <Button theme={ButtonThemes.blue} size={ButtonSizes.small} onClick={() => setShowReviewDispatch(true)}>Dispatch Week</Button>
                </div>
            </div>

        </div>

        <WeekSummaryTiles summary={summaryTiles} isLoading={isLoadingSummaryTiles} />

        <div className='linehaul-scheduler-container-dispatch-board'>
            <DispatchBoard />
        </div>
    </ContentContainer>
        <UpdateWeek
            isOpen={isUpdateWeekOpen}
            onClose={() => setIsUpdateWeekOpen(false)}
            isLoading={false}
            refetchGetWeeks={refetchWeeks}
            isEdit={isEditWeek}
            editWeek={editWeek}
        />
        <UpdateSidebar
            isOpen={isCreateLaneOpen}
            title="Create Lane"
            onClose={() => {
                setIsCreateLaneOpen(false);
            }}
            data={{ lane: {}, drops: [] }}
            isLoading={false}
            terminalMap={{}}
            isEditMode={"create"}
            refetchLane={() => { }}
            refetchLaneByDay={() => {
                refetchLanesByDay();
                refetchSummaryTiles();
            }}
            handleAddDrop={() => { }}
        />
        <TargetWeek
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            weekStartDate={weekStartDate}
            division={staticLoadState.selectedDivision}
            onCloneSuccess={refetchWeeks}
        />
        <ActivityLogSidebar
            isOpen={showActivityLog}
            onClose={() => setShowActivityLog(false)}
        />
        <ReviewDispatchSidebar
            isOpen={showReviewDispatch}
            onClose={() => setShowReviewDispatch(false)}
            onDispatchSuccess={() => {
                refetchWeeks();
                refetchSummaryTiles();
            }}
        />
    </>
};

export default LinehaulScheduler;
